/**
 * Deploy version detector — periodically polls /api/public/health and
 * compares the build hash. On mismatch records "stale bundle" so the UI
 * can offer a safe reload. No automatic reload — UX first.
 */
import { useSyncExternalStore } from "react";
import {
  captureRecoverySnapshot,
  hasAttemptedRecovery,
  markRecoveryAttempted,
} from "@/lib/stability/sessionRecovery";

export interface DeployState {
  current: string;
  latest: string | null;
  stale: boolean;
  lastCheckedAt: number;
}

const CURRENT = (import.meta.env.VITE_BUILD_HASH as string | undefined) ?? "dev";
let state: DeployState = { current: CURRENT, latest: null, stale: false, lastCheckedAt: 0 };
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

async function check() {
  if (typeof fetch === "undefined") return;
  try {
    const res = await fetch("/api/public/health", { cache: "no-store" });
    if (!res.ok) return;
    const json = (await res.json()) as { build?: string };
    const latest = json.build ?? null;
    const stale = !!latest && latest !== CURRENT && CURRENT !== "dev";
    state = { current: CURRENT, latest, stale, lastCheckedAt: Date.now() };
    emit();
    if (stale && !hasAttemptedRecovery()) {
      try { captureRecoverySnapshot(); } catch { /* noop */ }
      try { markRecoveryAttempted(); } catch { /* noop */ }
      // Defer slightly so listeners/UI can render the recovery prompt.
      setTimeout(() => {
        try { void safeReload(); } catch { /* noop */ }
      }, 1500);
    }
  } catch {
    /* offline / health unreachable — ignore */
  }
}

let started = false;
export function initDeployWatcher() {
  if (started || typeof window === "undefined") return;
  started = true;
  void check();
  setInterval(() => { void check(); }, 5 * 60_000);
  window.addEventListener("focus", () => { void check(); });
}

export function getDeployState(): DeployState {
  return state;
}

export function useDeployState(): DeployState {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => state,
    () => state,
  );
}

/** Cache cleanup + hard reload. Use sparingly (user gesture only). */
export async function safeReload() {
  if (typeof window === "undefined") return;
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* noop */
  }
  window.location.reload();
}
